import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, AlertCircle, CheckCircle2, Clock, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTION_LABELS = {
  upload_gtfs: 'Subida GTFS',
  chunked_upload_complete: 'Subida GTFS (fragmentada)',
  import_url: 'Importación desde URL',
  export_gtfs: 'Exportación GTFS',
  export_netex: 'Exportación NeTEx',
};

const STATUS_COLORS = {
  success: 'bg-emerald-100 text-emerald-800',
  error: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
};

const STATUS_ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: Clock,
};

const SystemLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '200');

      const res = await axios.get(`${API_URL}/admin/system-logs?${params.toString()}`);
      setLogs(res.data);
    } catch (err) {
      toast.error('Error al cargar logs del sistema');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [actionFilter, statusFilter]);

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (ms) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatBytes = (b) => {
    if (!b) return '-';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  };

  const errorCount = logs.filter(l => l.status === 'error').length;
  const successCount = logs.filter(l => l.status === 'success').length;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="system-logs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Logs del Sistema</h1>
          <p className="text-sm text-slate-500 mt-1">Registro de operaciones: uploads, imports, exports y errores</p>
        </div>
        <Button data-testid="refresh-logs-button" variant="outline" onClick={fetchLogs} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Total</p>
                <p className="text-2xl font-bold" data-testid="logs-total-count">{logs.length}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 uppercase font-medium">Exitosos</p>
                <p className="text-2xl font-bold text-emerald-700" data-testid="logs-success-count">{successCount}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 uppercase font-medium">Errores</p>
                <p className="text-2xl font-bold text-red-700" data-testid="logs-error-count">{errorCount}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-52" data-testid="filter-action">
            <SelectValue placeholder="Filtrar por acción" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las acciones</SelectItem>
            <SelectItem value="upload_gtfs">Subida GTFS</SelectItem>
            <SelectItem value="chunked_upload_complete">Subida fragmentada</SelectItem>
            <SelectItem value="import_url">Importación URL</SelectItem>
            <SelectItem value="export_gtfs">Exportación GTFS</SelectItem>
            <SelectItem value="export_netex">Exportación NeTEx</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="filter-status">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="success">Exitoso</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {loading && logs.length === 0 ? (
              <div className="p-8 text-center text-slate-400">Cargando logs...</div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No se encontraron logs</div>
            ) : (
              logs.map((log) => {
                const StatusIcon = STATUS_ICONS[log.status] || AlertCircle;
                const isExpanded = expandedLog === log.id;
                return (
                  <div key={log.id} data-testid={`log-entry-${log.id}`}>
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                      data-testid={`log-toggle-${log.id}`}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      <StatusIcon className={`w-4 h-4 flex-shrink-0 ${log.status === 'success' ? 'text-emerald-500' : log.status === 'error' ? 'text-red-500' : 'text-amber-500'}`} />
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[log.status] || 'bg-slate-100 text-slate-700'}`}>
                        {log.status === 'success' ? 'OK' : log.status === 'error' ? 'ERROR' : log.status}
                      </span>
                      <span className="text-sm font-medium text-slate-700 min-w-[180px]">
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                      <span className="text-xs text-slate-500 flex-1 truncate">
                        {log.project_name || log.project_id || '-'}
                      </span>
                      <span className="text-xs text-slate-400 min-w-[80px] text-right">
                        {formatDuration(log.duration_ms)}
                      </span>
                      <span className="text-xs text-slate-400 min-w-[140px] text-right">
                        {formatDate(log.timestamp)}
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="px-12 pb-4 bg-slate-50 border-t border-slate-100" data-testid={`log-details-${log.id}`}>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mt-3">
                          <div><span className="text-slate-500">Usuario:</span> <span className="font-medium">{log.user_name || '-'}</span></div>
                          <div><span className="text-slate-500">Proyecto ID:</span> <span className="font-mono text-xs">{log.project_id || '-'}</span></div>
                          {log.details?.filename && <div><span className="text-slate-500">Archivo:</span> <span className="font-medium">{log.details.filename}</span></div>}
                          {log.details?.url && <div className="col-span-2"><span className="text-slate-500">URL:</span> <span className="font-mono text-xs break-all">{log.details.url}</span></div>}
                          {log.details?.size_bytes && <div><span className="text-slate-500">Tamaño:</span> <span className="font-medium">{formatBytes(log.details.size_bytes)}</span></div>}
                          {log.details?.chunks && <div><span className="text-slate-500">Fragmentos:</span> <span className="font-medium">{log.details.chunks}</span></div>}
                          {log.details?.stats && (
                            <div className="col-span-2">
                              <span className="text-slate-500">Registros importados:</span>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {Object.entries(log.details.stats).map(([k, v]) => (
                                  <span key={k} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded">{k}: {v.toLocaleString()}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {log.error_message && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm font-medium text-red-800">Error: {log.error_message}</p>
                            {log.error_traceback && (
                              <pre className="mt-2 text-xs text-red-700 overflow-x-auto whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                                {log.error_traceback}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemLogsPage;
